import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { copapp, shiftwave } from '../../utils/downloads';
import './About.scss';

const values = [
    {
        icon: <TouchAppRoundedIcon />,
        title: 'User-Focused Design',
        description: 'We design our apps with users in mind, prioritizing intuitive interfaces and seamless experiences. Every feature is crafted to solve real problems and enhance daily life.',
    },
    {
        icon: <VerifiedRoundedIcon />,
        title: 'Innovation & Quality',
        description: 'We\'re constantly pushing boundaries with innovative features while maintaining the highest standards of quality. Our apps are thoroughly tested to ensure reliability.',
    },
    {
        icon: <LockRoundedIcon />,
        title: 'Privacy & Trust',
        description: 'We respect our users\' privacy and are transparent about data practices. We build trust through clear communication and honest business practices.',
    },
];

const About: React.FC = () => (
    <div className="about-page">
        <section className="about-page__hero">
            <Container maxWidth="md">
                <span className="eyebrow">About us</span>
                <Typography variant="h2" component="h1">
                    Apps for people whose week doesn't start on Monday
                </Typography>
                <Typography className="about-page__lead">
                    MyProjectX makes CopApp! and ShiftWave, the calendar apps that keep rotating shift schedules simple
                    for police officers, firefighters, nurses and everyone else who works around the clock.
                </Typography>
            </Container>
        </section>

        <Container maxWidth="lg">
            <section className="about-page__story">
                <div>
                    <h2>Our story</h2>
                    <p>
                        MyProjectX was founded with a clear focus on creating intuitive, user-friendly applications. We
                        are passionate about simplicity and believe great technology should feel effortless, helping
                        people accomplish more with less friction in their daily lives.
                    </p>
                    <p>
                        It started with CopApp!, a way for officers to throw out the paper schedule taped to the fridge.
                        Its users shared their rotations with us, and the list of ready-made schedules grew to hundreds
                        of departments and teams.
                    </p>
                    <p>
                        ShiftWave is the next generation: everything we learned from CopApp!, rebuilt with a home screen
                        widget, built-in holidays, calendar export, new themes and more.
                    </p>
                </div>

                <div className="about-page__apps">
                    {[shiftwave, copapp].map((app) => (
                        <RouterLink
                            key={app.id}
                            to={app.id === 'shiftwave' ? '/' : { pathname: '/', hash: '#copapp' }}
                            className="about-page__app"
                        >
                            <img src={app.icon} alt="" width={64} height={64} />
                            <div>
                                <h3>{app.name}</h3>
                                <p>{app.description}</p>
                            </div>
                            <ArrowForwardRoundedIcon />
                        </RouterLink>
                    ))}
                </div>
            </section>

            <section className="about-page__values">
                <h2>What we care about</h2>
                <div className="about-page__value-grid">
                    {values.map((v) => (
                        <div key={v.title} className="about-page__value">
                            <span className="about-page__icon">{v.icon}</span>
                            <h3>{v.title}</h3>
                            <p>{v.description}</p>
                        </div>
                    ))}
                </div>
            </section>
        </Container>
    </div>
);

export default About;
